import { useState, useEffect, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { doc, getDoc, updateDoc, serverTimestamp, collection, query, orderBy, onSnapshot } from 'firebase/firestore';
import { ref, uploadBytes, getDownloadURL } from 'firebase/storage';
import { db, storage } from '../../firebase';
import Layout from '../../components/ui/Layout';
import PageHeader from '../../components/ui/PageHeader';
import Card from '../../components/ui/Card';
import Button from '../../components/ui/Button';
import ServiceScheduleEditor from './ServiceScheduleEditor';
import { getCategoryLabel } from '../../constants/categories';
import { getSchemaFor, isSpecsComplete } from '../../constants/specSchemas';
import {
  IconArrowLeft, IconTractor, IconUpload, IconFileText,
  IconExternalLink, IconTrash, IconPlus, IconAlertTriangle,
  IconCheck, IconCircle,
} from '@tabler/icons-react';

export default function ModelDetail() {
  const { makeId, modelId } = useParams();
  const navigate = useNavigate();
  const [model, setModel] = useState(null);
  const [make, setMake] = useState(null);
  const [subCategories, setSubCategories] = useState([]);
  const [intervals, setIntervals] = useState([]);
  const [loading, setLoading] = useState(true);
  const [uploading, setUploading] = useState({ hero: false, operator: false, workshop: false });

  const heroInputRef = useRef();
  const operatorInputRef = useRef();
  const workshopInputRef = useRef();

  const loadAll = async () => {
    const [modelSnap, makeSnap] = await Promise.all([
      getDoc(doc(db, 'makes', makeId, 'models', modelId)),
      getDoc(doc(db, 'makes', makeId)),
    ]);
    if (modelSnap.exists()) setModel({ id: modelSnap.id, ...modelSnap.data() });
    if (makeSnap.exists()) setMake({ id: makeSnap.id, ...makeSnap.data() });
    setLoading(false);
  };

  useEffect(() => { loadAll(); }, [makeId, modelId]);

  useEffect(() => {
    if (!model?.category) return;
    const q = query(collection(db, 'categories', model.category, 'subCategories'), orderBy('order'));
    const unsub = onSnapshot(q, snap => setSubCategories(snap.docs.map(d => ({ id: d.id, ...d.data() }))));
    return unsub;
  }, [model?.category]);

  useEffect(() => {
    const q = query(collection(db, 'makes', makeId, 'models', modelId, 'serviceIntervals'), orderBy('triggerHours'));
    const unsub = onSnapshot(q, snap => setIntervals(snap.docs.map(d => ({ id: d.id, ...d.data() }))));
    return unsub;
  }, [makeId, modelId]);

  const updateModel = async (changes) => {
    await updateDoc(doc(db, 'makes', makeId, 'models', modelId), { ...changes, updatedAt: serverTimestamp() });
    await loadAll();
  };

  const handleFileUpload = async (file, kind) => {
    if (!file) return;
    setUploading(prev => ({ ...prev, [kind]: true }));
    try {
      const path = 'models/' + modelId + '/' + kind + '_' + Date.now() + '_' + file.name;
      const storageRef = ref(storage, path);
      await uploadBytes(storageRef, file);
      const url = await getDownloadURL(storageRef);
      const fieldMap = { hero: 'heroImageUrl', operator: 'operatorManualUrl', workshop: 'workshopManualUrl' };
      await updateModel({ [fieldMap[kind]]: url });
    } catch (err) {
      console.error(err);
      alert('Upload failed: ' + err.message);
    }
    setUploading(prev => ({ ...prev, [kind]: false }));
  };

  if (loading) return <Layout><div style={{ padding: 40, color: 'var(--color-text-tertiary)' }}>Loading...</div></Layout>;
  if (!model || !make) return (
    <Layout>
      <PageHeader title="Model not found" />
      <div style={{ padding: 28 }}>
        <Button onClick={() => navigate('/library')} icon={<IconArrowLeft size={14} stroke={1.5} />}>Back to library</Button>
      </div>
    </Layout>
  );

  const subCategoryName = model.subCategory && (subCategories.find(s => s.code === model.subCategory)?.name || model.subCategory);

  const hasHero = !!model.heroImageUrl;
  const hasOperator = !!model.operatorManualUrl;
  const hasWorkshop = !!model.workshopManualUrl;
  const hasSubCategory = !!model.subCategory;
  const specsComplete = isSpecsComplete(model.category, model.subCategory, model.specs);
  const scheduleComplete = !!model.serviceScheduleComplete;

  const breadcrumb = (
    <div style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: 12, color: 'var(--color-text-tertiary)', marginBottom: 6 }}>
      <span onClick={() => navigate('/library')} style={{ cursor: 'pointer' }}>Library</span>
      <span>/</span>
      <span onClick={() => navigate('/library/makes/' + makeId)} style={{ cursor: 'pointer' }}>{make.name}</span>
      <span>/</span>
      <span onClick={() => navigate('/library/makes/' + makeId + '?type=' + model.category)} style={{ cursor: 'pointer' }}>{getCategoryLabel(model.category)}</span>
      {model.subCategory && (<><span>/</span><span onClick={() => navigate('/library/makes/' + makeId + '?type=' + model.category + '&sub=' + model.subCategory)} style={{ cursor: 'pointer' }}>{subCategoryName}</span></>)}
      <span>/</span>
      <span style={{ color: 'var(--color-text-primary)' }}>{model.name}</span>
    </div>
  );

  return (
    <Layout>
      <PageHeader
        title={<div>{breadcrumb}<div>{model.name}</div></div>}
        subtitle={make.name + ' · ' + getCategoryLabel(model.category) + (subCategoryName ? ' · ' + subCategoryName : '') + (model.yearRange ? ' · ' + model.yearRange : '')}
        actions={<Button variant="secondary" onClick={() => navigate('/library/makes/' + makeId + '?type=' + model.category + (model.subCategory ? '&sub=' + model.subCategory : ''))} icon={<IconArrowLeft size={14} stroke={1.5} />}>Back</Button>}
      />

      {!model.subCategory && (
        <div style={{ margin: '20px 28px 0', padding: '14px 18px', background: 'var(--color-warning-light)', border: '0.5px solid var(--color-warning)', borderRadius: 'var(--radius-lg)', display: 'flex', alignItems: 'center', gap: 12 }}>
          <IconAlertTriangle size={18} stroke={1.5} color="var(--color-warning)" />
          <div style={{ flex: 1, fontSize: 13, color: 'var(--color-warning)' }}>
            This model doesn't have a sub-category yet. Pick one below to complete it.
          </div>
          {subCategories.length === 0 ? (
            <div style={{ fontSize: 12, color: 'var(--color-text-tertiary)' }}>No sub-categories defined for {getCategoryLabel(model.category)} — add in Settings.</div>
          ) : (
            <select value={model.subCategory || ''} onChange={e => updateModel({ subCategory: e.target.value })} style={{ maxWidth: 200 }}>
              <option value="">Pick one...</option>
              {subCategories.map(sc => <option key={sc.id} value={sc.code}>{sc.name}</option>)}
            </select>
          )}
        </div>
      )}

      <div style={{ padding: 28 }}>
        <CompletenessBar
          checks={[
            { label: 'Hero image', done: hasHero },
            { label: 'Operator manual', done: hasOperator },
            { label: 'Workshop manual', done: hasWorkshop },
            { label: 'Sub-category', done: hasSubCategory },
            { label: 'Specs', done: specsComplete },
            { label: 'Service schedule', done: scheduleComplete },
          ]}
        />

        {/* Top row: hero (left) + documents/specs (right) */}
        <div style={{ display: 'grid', gridTemplateColumns: '320px 1fr', gap: 20, marginTop: 20 }}>
          <div>
            <SectionLabel>Hero image</SectionLabel>
            <Card padding={0} style={{ overflow: 'hidden' }}>
              <div style={{ aspectRatio: '1', background: 'var(--color-bg-secondary)', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                {model.heroImageUrl ? <img src={model.heroImageUrl} alt={model.name} style={{ width: '100%', height: '100%', objectFit: 'cover' }} /> : <IconTractor size={72} stroke={0.8} color="var(--color-text-tertiary)" />}
              </div>
              <div style={{ padding: 12 }}>
                <input ref={heroInputRef} type="file" accept="image/*" style={{ display: 'none' }} onChange={e => handleFileUpload(e.target.files?.[0], 'hero')} />
                <Button fullWidth variant="secondary" loading={uploading.hero} icon={<IconUpload size={14} stroke={1.5} />} onClick={() => heroInputRef.current?.click()}>
                  {model.heroImageUrl ? 'Replace image' : 'Upload image'}
                </Button>
              </div>
            </Card>
          </div>

          <div>
            <SectionLabel>Documents</SectionLabel>
            <Card>
              <DocumentRow label="Operator's manual" url={model.operatorManualUrl} loading={uploading.operator} onUpload={() => operatorInputRef.current?.click()} onRemove={() => updateModel({ operatorManualUrl: null })} />
              <input ref={operatorInputRef} type="file" accept="application/pdf" style={{ display: 'none' }} onChange={e => handleFileUpload(e.target.files?.[0], 'operator')} />
              <div style={{ height: 1, background: 'var(--color-border-light)', margin: '12px 0' }} />
              <DocumentRow label="Workshop manual" url={model.workshopManualUrl} loading={uploading.workshop} onUpload={() => workshopInputRef.current?.click()} onRemove={() => updateModel({ workshopManualUrl: null })} />
              <input ref={workshopInputRef} type="file" accept="application/pdf" style={{ display: 'none' }} onChange={e => handleFileUpload(e.target.files?.[0], 'workshop')} />
            </Card>

            <SectionLabel style={{ marginTop: 20 }}>Specifications</SectionLabel>
            <SpecsEditor
              category={model.category}
              subCategory={model.subCategory}
              specs={model.specs || {}}
              onChange={newSpecs => updateModel({ specs: newSpecs })}
            />
          </div>
        </div>

        {/* Full-width service schedule */}
        <div style={{ marginTop: 24 }}>
          <SectionLabel>Service schedule</SectionLabel>
          <ServiceScheduleEditor makeId={makeId} modelId={modelId} />

          {intervals.length > 0 && (
            <div style={{ marginTop: 12, padding: '12px 16px', background: 'var(--color-bg-primary)', border: '0.5px solid var(--color-border-light)', borderRadius: 'var(--radius-lg)' }}>
              <label style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: 13, cursor: 'pointer' }}>
                <input
                  type="checkbox"
                  checked={!!model.serviceScheduleComplete}
                  onChange={e => updateModel({ serviceScheduleComplete: e.target.checked })}
                  style={{ width: 14, height: 14, cursor: 'pointer' }}
                />
                <span style={{ color: model.serviceScheduleComplete ? 'var(--color-success)' : 'var(--color-text-secondary)' }}>
                  {model.serviceScheduleComplete
                    ? <><IconCheck size={13} stroke={2} style={{ verticalAlign: 'middle' }} /> Service schedule complete — this is the full set of intervals for this model</>
                    : 'Mark service schedule complete (this is the full set of intervals for this model)'}
                </span>
              </label>
            </div>
          )}
        </div>
      </div>
    </Layout>
  );
}

function CompletenessBar({ checks }) {
  const done = checks.filter(c => c.done).length;
  const total = checks.length;
  const pct = Math.round((done / total) * 100);

  return (
    <Card padding={0}>
      <div style={{ padding: '14px 20px', display: 'flex', alignItems: 'center', gap: 16 }}>
        <div style={{ flexShrink: 0 }}>
          <div style={{ fontSize: 11, color: 'var(--color-text-tertiary)', textTransform: 'uppercase', letterSpacing: '0.04em', marginBottom: 2 }}>Completeness</div>
          <div style={{ fontSize: 18, fontWeight: 500, color: pct === 100 ? 'var(--color-success)' : 'var(--color-text-primary)' }}>{pct}%</div>
        </div>
        <div style={{ flex: 1, display: 'flex', flexWrap: 'wrap', gap: 8 }}>
          {checks.map(c => (
            <span key={c.label} style={{
              display: 'inline-flex', alignItems: 'center', gap: 4,
              fontSize: 11, padding: '3px 9px', borderRadius: 'var(--radius-full)',
              background: c.done ? 'var(--color-success-light)' : 'var(--color-bg-tertiary)',
              color: c.done ? 'var(--color-success)' : 'var(--color-text-tertiary)',
            }}>
              {c.done ? <IconCheck size={11} stroke={2} /> : <IconCircle size={11} stroke={1.5} />}
              {c.label}
            </span>
          ))}
        </div>
      </div>
    </Card>
  );
}

function SectionLabel({ children, style = {} }) {
  return <div style={{ fontSize: 10, fontWeight: 500, color: 'var(--color-text-tertiary)', letterSpacing: '0.04em', textTransform: 'uppercase', marginBottom: 8, ...style }}>{children}</div>;
}

function DocumentRow({ label, url, loading, onUpload, onRemove }) {
  return (
    <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
      <IconFileText size={20} stroke={1.5} color={url ? 'var(--color-brand)' : 'var(--color-text-tertiary)'} />
      <div style={{ flex: 1 }}>
        <div style={{ fontSize: 13 }}>{label}</div>
        <div style={{ fontSize: 11, color: 'var(--color-text-tertiary)' }}>{url ? 'PDF uploaded' : 'No PDF uploaded'}</div>
      </div>
      {url && <a href={url} target="_blank" rel="noreferrer" style={{ display: 'flex', alignItems: 'center', gap: 4, fontSize: 12, color: 'var(--color-brand)' }}><IconExternalLink size={13} stroke={1.5} /> View</a>}
      {url && <div onClick={onRemove} style={{ cursor: 'pointer', color: 'var(--color-text-tertiary)' }}><IconTrash size={14} stroke={1.5} /></div>}
      <Button variant="secondary" size="sm" loading={loading} onClick={onUpload}>{url ? 'Replace' : 'Upload'}</Button>
    </div>
  );
}

function SpecsEditor({ category, subCategory, specs, onChange }) {
  const schema = getSchemaFor(category, subCategory);
  const schemaFields = schema?.fields || [];
  const schemaKeys = new Set(schemaFields.map(f => f.key));
  const customEntries = Object.entries(specs).filter(([k]) => !schemaKeys.has(k));

  const [newKey, setNewKey] = useState('');
  const [newValue, setNewValue] = useState('');

  const setField = (key, value) => onChange({ ...specs, [key]: value });
  const removeField = (key) => { const next = { ...specs }; delete next[key]; onChange(next); };

  const addCustom = () => {
    if (!newKey.trim()) return;
    onChange({ ...specs, [newKey.trim()]: newValue.trim() });
    setNewKey(''); setNewValue('');
  };

  return (
    <Card>
      {schemaFields.length > 0 ? (
        <>
          <div style={{ fontSize: 10, color: 'var(--color-text-tertiary)', textTransform: 'uppercase', letterSpacing: '0.04em', marginBottom: 8 }}>Required ({schemaFields.filter(f => f.required).length})</div>
          {schemaFields.map(field => (
            <div key={field.key} style={{ display: 'flex', gap: 12, marginBottom: 8, alignItems: 'center' }}>
              <div style={{ flex: '0 0 40%', fontSize: 12, color: 'var(--color-text-secondary)' }}>
                {field.label}
                {field.required && <span style={{ color: 'var(--color-danger)', marginLeft: 4 }}>*</span>}
              </div>
              <input
                type={field.type === 'number' ? 'number' : 'text'}
                step="0.01"
                value={specs[field.key] ?? ''}
                onChange={e => setField(field.key, e.target.value)}
                style={{ flex: 1 }}
                placeholder={'e.g. ' + (field.type === 'number' ? '0' : '')}
              />
            </div>
          ))}
        </>
      ) : (
        <div style={{ fontSize: 12, color: 'var(--color-text-tertiary)', marginBottom: 12 }}>
          No spec schema defined for this sub-category yet — using custom fields only.
        </div>
      )}

      {customEntries.length > 0 && (
        <div style={{ marginTop: schemaFields.length > 0 ? 16 : 0, paddingTop: schemaFields.length > 0 ? 12 : 0, borderTop: schemaFields.length > 0 ? '0.5px solid var(--color-border-light)' : 'none' }}>
          <div style={{ fontSize: 10, color: 'var(--color-text-tertiary)', textTransform: 'uppercase', letterSpacing: '0.04em', marginBottom: 8 }}>Custom fields</div>
          {customEntries.map(([key, value]) => (
            <div key={key} style={{ display: 'flex', gap: 8, marginBottom: 6, alignItems: 'center' }}>
              <input value={key} disabled style={{ flex: '0 0 40%', background: 'var(--color-bg-secondary)' }} />
              <input value={value} onChange={e => setField(key, e.target.value)} style={{ flex: 1 }} />
              <div onClick={() => removeField(key)} style={{ cursor: 'pointer', padding: 6, color: 'var(--color-text-tertiary)' }}><IconTrash size={14} stroke={1.5} /></div>
            </div>
          ))}
        </div>
      )}

      <div style={{ display: 'flex', gap: 8, alignItems: 'center', paddingTop: 12, marginTop: 12, borderTop: '0.5px solid var(--color-border-light)' }}>
        <input value={newKey} onChange={e => setNewKey(e.target.value)} placeholder="Custom field name" style={{ flex: '0 0 40%' }} />
        <input value={newValue} onChange={e => setNewValue(e.target.value)} placeholder="Value" onKeyDown={e => { if (e.key === 'Enter') { e.preventDefault(); addCustom(); } }} style={{ flex: 1 }} />
        <Button size="sm" onClick={addCustom} icon={<IconPlus size={12} stroke={2} />}>Add</Button>
      </div>
    </Card>
  );
}
